// Hearth — auth screen. Sign in or create an account to host/join campaigns.
import { useMemo, useState } from "react";
import { useAuth } from "../hooks/useAuth";
import { ApiError } from "../lib/api";

// Client-side mirror of the backend register rules (hearth/auth.register) so the
// form can guide before it ever round-trips.
const USER_RE = /^[A-Za-z0-9_.-]+$/;
function usernameHint(v: string): string | null {
  if (!v) return null;
  if (v.length < 3) return "At least 3 characters.";
  if (v.length > 24) return "24 characters or fewer.";
  if (!USER_RE.test(v)) return "Letters, numbers, and _ . - only.";
  return null;
}

// Route an API error message to the field it's about, so it lands where the user
// is looking instead of only in a general banner.
function fieldForError(msg: string): "username" | "password" | null {
  const m = msg.toLowerCase();
  if (m.includes("password")) return "password";
  if (m.includes("username") || m.includes("taken") || m.includes("exists") || m.includes("already")) return "username";
  return null;
}

export function AuthScreen({ onGuest }: { onGuest?: () => void }) {
  const { login, register } = useAuth();
  const [mode, setMode] = useState<"login" | "register">("login");
  const [username, setUsername] = useState("");
  const [displayName, setDisplayName] = useState("");
  const [password, setPassword] = useState("");
  const [showPw, setShowPw] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [errorField, setErrorField] = useState<"username" | "password" | null>(null);

  const uHint = mode === "register" ? usernameHint(username.trim()) : null;
  const pwTooShort = mode === "register" && password.length > 0 && password.length < 6;

  // Submit is enabled only once the required fields are non-empty and (on register)
  // pass the local format rules — matching what the server will accept.
  const canSubmit = useMemo(() => {
    if (busy) return false;
    if (!username.trim() || !password) return false;
    if (mode === "register" && (uHint !== null || password.length < 6)) return false;
    return true;
  }, [busy, username, password, mode, uHint]);

  async function submit(e: React.FormEvent) {
    e.preventDefault();
    if (!canSubmit) return;
    setBusy(true);
    setError(null);
    setErrorField(null);
    try {
      if (mode === "login") await login(username.trim(), password);
      else await register(username.trim(), password, displayName.trim() || undefined);
    } catch (err) {
      const msg = err instanceof ApiError ? err.message : "Something went wrong";
      setError(msg);
      setErrorField(fieldForError(msg));
    } finally {
      setBusy(false);
    }
  }

  function switchMode(next: "login" | "register") {
    setMode(next);
    setError(null);
    setErrorField(null);
  }

  return (
    <div className="auth-wrap">
      <div className="auth-card">
        <div className="auth-brand">🜂 Hearth</div>
        <div className="auth-tag">An AI-run table for Dungeons &amp; Dragons</div>

        <div className="auth-tabs">
          <button className={`auth-tab${mode === "login" ? " auth-tab--on" : ""}`}
            onClick={() => switchMode("login")}>Sign in</button>
          <button className={`auth-tab${mode === "register" ? " auth-tab--on" : ""}`}
            onClick={() => switchMode("register")}>Create account</button>
        </div>

        <form onSubmit={submit} className="auth-form">
          <label className="auth-label">Username
            <input className="auth-input" value={username} autoFocus autoComplete="username"
              onChange={(e) => { setUsername(e.target.value); if (errorField === "username") { setError(null); setErrorField(null); } }}
              placeholder="your handle" />
          </label>
          {uHint && <div className="auth-fieldhint" style={{ fontSize: 12, color: "var(--ember-bright, var(--flame))", marginTop: -6 }}>{uHint}</div>}
          {errorField === "username" && error && <div className="auth-error">{error}</div>}

          {mode === "register" && (
            <label className="auth-label">Display name <span className="auth-opt">(optional)</span>
              <input className="auth-input" value={displayName}
                onChange={(e) => setDisplayName(e.target.value)} placeholder="shown at the table" />
            </label>
          )}

          <label className="auth-label">Password
            <span className="auth-pw-wrap" style={{ position: "relative", display: "block" }}>
              <input className="auth-input" type={showPw ? "text" : "password"} value={password}
                autoComplete={mode === "login" ? "current-password" : "new-password"}
                onChange={(e) => { setPassword(e.target.value); if (errorField === "password") { setError(null); setErrorField(null); } }}
                placeholder="••••••" style={{ paddingRight: 52 }} />
              <button type="button" className="auth-pw-toggle"
                onClick={() => setShowPw((v) => !v)}
                aria-label={showPw ? "Hide password" : "Show password"}
                style={{ position: "absolute", right: 8, top: "50%", transform: "translateY(-50%)",
                  background: "none", border: "none", cursor: "pointer", fontSize: 12,
                  color: "var(--ink-mute)", padding: 4 }}>
                {showPw ? "Hide" : "Show"}
              </button>
            </span>
          </label>
          {mode === "register" && (
            <div className="auth-fieldhint" style={{ fontSize: 12, color: pwTooShort ? "var(--ember-bright, var(--flame))" : "var(--ink-mute)", marginTop: -6 }}>
              At least 6 characters.
            </div>
          )}
          {errorField === "password" && error && <div className="auth-error">{error}</div>}

          {error && errorField === null && <div className="auth-error">{error}</div>}

          <button className="btn btn--primary auth-submit" type="submit" disabled={!canSubmit}>
            {busy && <span className="spinner" style={{ marginRight: 8 }} aria-hidden />}
            {mode === "login" ? "Sign in" : "Create account & enter"}
          </button>
        </form>

        {onGuest && (
          <button className="auth-guest" onClick={onGuest}>
            Browse campaigns as a guest →
          </button>
        )}
        <div className="auth-note">
          Accounts let you host campaigns, claim a seat, and rejoin where you left off.
        </div>
      </div>
    </div>
  );
}
